import { useEffect, ReactNode } from "react";
import { Link } from "react-router-dom";

type LegalPageProps = {
  title: string;
  metaTitle: string;
  metaDescription: string;
  canonical: string;
  lastUpdated?: string;
  intro?: string;
  children: ReactNode;
};

const NAV = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms of Service" },
  { to: "/responsible-gambling", label: "Responsible Gambling" },
  { to: "/billing-terms", label: "Billing Terms" },
  { to: "/refund-policy", label: "Refund Policy" },
];

export default function LegalPage({
  title,
  metaTitle,
  metaDescription,
  canonical,
  lastUpdated = "May 2026",
  intro,
  children,
}: LegalPageProps) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = metaTitle;

    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };

    const desc = setMeta("description", metaDescription);
    const ogTitle = setMeta("og:title", metaTitle, "property");
    const ogDesc = setMeta("og:description", metaDescription, "property");
    const ogType = setMeta("og:type", "article", "property");

    let canonicalEl = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const created = !canonicalEl;
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    const prevHref = canonicalEl.getAttribute("href");
    canonicalEl.setAttribute("href", `https://aleaedge.com${canonical}`);

    return () => {
      document.title = prevTitle;
      desc.remove();
      ogTitle.remove();
      ogDesc.remove();
      ogType.remove();
      if (created) canonicalEl?.remove();
      else if (prevHref) canonicalEl?.setAttribute("href", prevHref);
    };
  }, [metaTitle, metaDescription, canonical]);

  return (
    <div className="container max-w-5xl py-10 sm:py-14">
      <div className="grid lg:grid-cols-[220px_minmax(0,1fr)] gap-10">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Legal</div>
            <nav className="flex flex-col gap-1 text-sm">
              {NAV.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`px-3 py-2 rounded-lg transition ${
                    canonical === l.to
                      ? "bg-secondary text-foreground font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        <article className="min-w-0">
          <header className="mb-8 pb-8 border-b border-border/60">
            <div className="text-xs uppercase tracking-wider text-accent font-semibold mb-3">
              AleaEdge · Legal
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              {title}
            </h1>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mt-4">
              Last updated {lastUpdated}
            </p>
            {intro && (
              <p className="mt-5 text-base text-muted-foreground leading-relaxed max-w-3xl">
                {intro}
              </p>
            )}
          </header>

          <div className="legal-prose space-y-8 text-[15px] leading-relaxed text-muted-foreground">
            {children}
          </div>

          <nav className="mt-14 pt-8 border-t border-border/60 lg:hidden">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
              More legal
            </div>
            <div className="grid grid-cols-2 gap-2">
              {NAV.filter((l) => l.to !== canonical).map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-3 py-2.5 rounded-lg bg-secondary/40 hover:bg-secondary text-sm text-foreground transition"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </nav>

          <p className="mt-10 text-xs text-muted-foreground">
            Questions? Contact{" "}
            <a href="mailto:support@aleaedge.com" className="text-primary hover:underline">
              support@aleaedge.com
            </a>
            .
          </p>
        </article>
      </div>
    </div>
  );
}

export function Section({ id, title, children }: { id?: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-display text-xl sm:text-2xl font-semibold text-foreground tracking-tight mb-3">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export function SubSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="font-display text-base font-semibold text-foreground mb-2">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
